import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { usePath, navigate, parseRoute } from '../../lib/router';
import {
  Building, Search, Plus, X, Edit3, Archive, RotateCcw,
  Users, Mail, Phone, MapPin, Settings, Shield, UserCheck,
  UserX, ArrowRight, Activity, Globe, Eye, FileText, Check, AlertTriangle, Play, CheckCircle
} from 'lucide-react';
import Modal from '../shared/Modal';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';

interface Organization {
  id: string;
  workspaceId: string;
  type: 'employer' | 'sponsor' | 'referrer';
  entityKind: 'organization' | 'individual';
  name: string;
  legalName?: string;
  slug?: string;
  logoUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: {
    street: string;
    apartment?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  status: 'active' | 'inactive' | 'archived';
  primaryAccountUid?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface Member {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  primaryRole: string;
  workspaceRole: string;
  membershipStatus: string;
  title: string;
  isPrimaryAccount: boolean;
  profilePhotoUrl?: string;
}

interface OrganizationsManagerProps {
  canEdit?: boolean;
}

export default function OrganizationsManager({ canEdit = true }: OrganizationsManagerProps = {}) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'employer' | 'sponsor' | 'referrer'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgMembers, setOrgMembers] = useState<Member[]>([]);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync selectedOrg with URL path.
  //
  // #1125 Item 5 (sibling of the EmployersManager fix): the detail state
  // must not be a dependency of this route-sync effect — a row click
  // sets selectedOrg while the path is still '/organizations', the
  // effect would re-run on that commit and clear it before the navigate
  // effect could move the route. Compare through a ref instead.
  const selectedOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedOrgIdRef.current = selectedOrg?.id ?? null;
  }, [selectedOrg]);

  useEffect(() => {
    if (currentRoute.type === 'organizations_detail') {
      const targetId = currentRoute.id;
      if (selectedOrgIdRef.current !== targetId) {
        const found = organizations.find(o => o.id === targetId);
        if (found) {
          setSelectedOrg(found);
          fetchMembers(found.id);
        }
      }
    } else if (currentPath === '/organizations') {
      if (selectedOrgIdRef.current) {
        setSelectedOrg(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, organizations]);

  const prevSelectedOrgRef = useRef(selectedOrg);

  // Sync URL path when selectedOrg changes
  useEffect(() => {
    if (selectedOrg?.id) {
      navigate(`/organizations/adminview/${selectedOrg.id}`);
    } else if (prevSelectedOrgRef.current && !selectedOrg && currentRoute.type === 'organizations_detail') {
      navigate('/organizations');
    }
    prevSelectedOrgRef.current = selectedOrg;
  }, [selectedOrg, currentRoute.type]);
  const [membersLoading, setOrgMembersLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'details' | 'members' | 'audit'>('details');

  // Member Action Modals
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showLinkUserModal, setShowAddLinkUserModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Form states
  const [form, setForm] = useState({
    name: '',
    legalName: '',
    type: 'employer' as 'employer' | 'sponsor' | 'referrer',
    entityKind: 'organization' as 'organization' | 'individual',
    contactEmail: '',
    contactPhone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    logoUrl: '',
  });

  // Invitation Form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    title: '',
    workspaceRole: 'member',
  });
  const [inviting, setInviting] = useState(false);

  // Linking existing user
  const [linkEmail, setLinkEmail] = useState('');
  const [linkedUser, setLinkedUser] = useState<any | null>(null);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkWorkspaceRole, setLinkWorkspaceRole] = useState('member');
  const [searchingUser, setSearchingUser] = useState(false);
  const [linkingUser, setLinkingUser] = useState(false);

  // Migration panel state
  const [migrationLogs, setMigrationLogs] = useState<string[]>([]);
  const [migrationResults, setMigrationResults] = useState<any | null>(null);
  const [migrating, setMigrating] = useState(false);

  useBodyScrollLock(showCreateModal || !!editingOrg || !!selectedOrg);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/admin/organizations?status=${statusFilter === 'all' ? '' : statusFilter}`;
      if (typeFilter !== 'all') url += `&type=${typeFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await safeFetch(url);
      setOrganizations(res.organizations || []);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, searchQuery]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const fetchMembers = async (orgId: string) => {
    setOrgMembersLoading(true);
    try {
      const res = await safeFetch(`/api/admin/organizations/${orgId}/members`);
      setOrgMembers(res.members || []);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to fetch members');
    } finally {
      setOrgMembersLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        legalName: form.legalName,
        type: form.type,
        entityKind: form.entityKind,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        address: {
          street: form.street,
          city: form.city,
          state: form.state,
          zip: form.zip,
          country: form.country,
        },
        logoUrl: form.logoUrl,
      };

      await safeFetch('/api/admin/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      notifyToast('success', 'Organization created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchOrgs();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to create organization');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;
    try {
      const payload = {
        name: form.name,
        legalName: form.legalName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        address: {
          street: form.street,
          city: form.city,
          state: form.state,
          zip: form.zip,
          country: form.country,
        },
        logoUrl: form.logoUrl,
      };

      await safeFetch(`/api/admin/organizations/${editingOrg.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      notifyToast('success', 'Organization updated successfully');
      setEditingOrg(null);
      resetForm();
      fetchOrgs();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to update organization');
    }
  };

  const handleArchive = async (id: string) => {
    if (!(await confirmDialog('Are you sure you want to archive this organization? All linked legacy users will lose administrative claims.'))) return;
    try {
      await safeFetch(`/api/admin/organizations/${id}/archive`, { method: 'POST' });
      notifyToast('success', 'Organization archived successfully');
      fetchOrgs();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to archive organization');
    }
  };

  const handleReactivate = async (id: string, name?: string) => {
    if (!(await confirmDialog(`Reactivate the organization ${name || 'this organization'}? It will return to the active organizations list.`))) return;
    try {
      await safeFetch(`/api/admin/organizations/${id}/reactivate`, { method: 'POST' });
      notifyToast('success', 'Organization reactivated successfully');
      fetchOrgs();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to reactivate organization');
    }
  };

  const handleInvitePrimaryAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setInviting(true);
    try {
      // First, create the user account (create-and-add creates the Auth account
      // when none exists; /members alone 404s for new emails)
      const res = await safeFetch('/api/workspaces/members/create-and-add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteForm.email.toLowerCase().trim(),
          role: inviteForm.workspaceRole,
          firstName: inviteForm.firstName.trim(),
          lastName: inviteForm.lastName.trim(),
        }),
      });

      const newUid = res.member?.uid;
      if (!newUid) throw new Error('User creation did not return a valid UID');

      // Now, assign them as organization member & primary account
      await safeFetch(`/api/admin/organizations/${selectedOrg.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: newUid,
          title: inviteForm.title,
          workspaceRole: inviteForm.workspaceRole,
        }),
      });

      notifyToast('success', 'Primary account invited and linked successfully');
      setShowAddMemberModal(false);
      resetInviteForm();
      fetchMembers(selectedOrg.id);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to invite primary account');
    } finally {
      setInviting(false);
    }
  };

  const handleSearchUser = async () => {
    if (!linkEmail) return;
    setSearchingUser(true);
    setLinkedUser(null);
    try {
      const res = await safeFetch(`/api/admin/users/by-email?email=${encodeURIComponent(linkEmail)}`);
      setLinkedUser(res.user);
    } catch (err: any) {
      notifyToast('error', 'User not found or query failed');
    } finally {
      setSearchingUser(false);
    }
  };

  const handleLinkUser = async () => {
    if (!selectedOrg || !linkedUser) return;
    setLinkingUser(true);
    try {
      await safeFetch(`/api/admin/organizations/${selectedOrg.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: linkedUser.uid,
          title: linkTitle,
          workspaceRole: linkWorkspaceRole,
        }),
      });

      notifyToast('success', 'User linked successfully and role converted');
      setShowAddLinkUserModal(false);
      setLinkEmail('');
      setLinkedUser(null);
      setLinkTitle('');
      fetchMembers(selectedOrg.id);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to link user');
    } finally {
      setLinkingUser(false);
    }
  };

  const handleRemoveMember = async (uid: string) => {
    if (!selectedOrg) return;
    if (!(await confirmDialog('Are you sure you want to remove this member? This will convert their role back to Applicant.'))) return;
    try {
      await safeFetch(`/api/admin/organizations/${selectedOrg.id}/members/${uid}`, { method: 'DELETE' });
      notifyToast('success', 'Member removed and role reverted safely.');
      fetchMembers(selectedOrg.id);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to remove member');
    }
  };

  const handleTransferOwnership = async (newOwnerUid: string) => {
    if (!selectedOrg) return;
    if (!(await confirmDialog('Are you sure you want to transfer primary account ownership to this user?'))) return;
    try {
      await safeFetch(`/api/admin/organizations/${selectedOrg.id}/transfer-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerUid }),
      });
      notifyToast('success', 'Ownership transferred successfully');
      fetchMembers(selectedOrg.id);
      // Refresh organization detail
      const orgRes = await safeFetch(`/api/admin/organizations/${selectedOrg.id}`);
      setSelectedOrg(orgRes.organization);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to transfer ownership');
    }
  };

  const runMigration = async (dryRun: boolean) => {
    setMigrating(true);
    setMigrationLogs([]);
    setMigrationResults(null);
    try {
      const res = await safeFetch('/api/admin/organizations/run-migration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      setMigrationResults(res.results);
      setMigrationLogs(res.results.logs || []);
      notifyToast('success', dryRun ? 'Dry run completed' : 'Migration executed successfully');
      fetchOrgs();
    } catch (err: any) {
      notifyToast('error', err.message || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', legalName: '', type: 'employer', entityKind: 'organization',
      contactEmail: '', contactPhone: '', street: '', city: '', state: '', zip: '', country: '', logoUrl: ''
    });
  };

  const resetInviteForm = () => {
    setInviteForm({ email: '', firstName: '', lastName: '', phone: '', title: '', workspaceRole: 'member' });
  };

  const openEdit = (org: Organization) => {
    setEditingOrg(org);
    setForm({
      name: org.name,
      legalName: org.legalName || '',
      type: org.type,
      entityKind: org.entityKind,
      contactEmail: org.contactEmail || '',
      contactPhone: org.contactPhone || '',
      street: org.address?.street || '',
      city: org.address?.city || '',
      state: org.address?.state || '',
      zip: org.address?.zip || '',
      country: org.address?.country || '',
      logoUrl: org.logoUrl || '',
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <Building className="h-6 w-6 text-brand-primary" />
            Organization Isolation Console
          </h1>
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">
            Manage Multi-Tenant Corporate Entities & Isolated Identity Roles
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Create Business Entity
            </button>
          )}
        </div>
      </div>


      {migrationResults && (
        <div className="bg-slate-50 text-slate-800 border border-slate-200 p-4 rounded-2xl font-mono text-xs space-y-2">
          <div className="flex justify-between border-b border-slate-800 pb-2">
            <span className="font-bold text-emerald-400">Migration Completed:</span>
            <span>{migrating ? 'Running...' : 'Finished'}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
            <div>Employers Migrated: <span className="text-white font-bold">{migrationResults.employers}</span></div>
            <div>Sponsors Migrated: <span className="text-white font-bold">{migrationResults.sponsors}</span></div>
            <div>Referrers Migrated: <span className="text-white font-bold">{migrationResults.referrers}</span></div>
            <div>Teammates Linked: <span className="text-white font-bold">{migrationResults.usersLinked}</span></div>
          </div>
          {migrationLogs.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1 border-t border-slate-800 pt-2 text-[10px]">
              {migrationLogs.map((l, i) => (
                <div key={i} className="text-slate-400">&gt; {l}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row items-center gap-4 shadow-sm">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by corporate name, legal name, email, phone, or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-brand-primary rounded-xl pl-10 pr-4 py-2.5 text-xs font-medium text-slate-900 placeholder:text-slate-400 transition-all outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Type Select */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-700 outline-none"
          >
            <option value="all">All Types</option>
            <option value="employer">Employer Orgs</option>
            <option value="sponsor">Sponsor Orgs</option>
            <option value="referrer">Referrer Orgs</option>
          </select>

          {/* Status Select */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-700 outline-none"
          >
            <option value="active">Active Orgs</option>
            <option value="archived">Archived Orgs</option>
            <option value="all">All Statuses</option>
          </select>
        </div>
      </div>

      {/* Grid List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 animate-pulse">
              <div className="h-6 w-32 bg-slate-200 rounded"></div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-slate-100 rounded"></div>
                <div className="h-4 w-2/3 bg-slate-100 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : organizations.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center flex flex-col items-center justify-center space-y-3 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
            <Building className="h-6 w-6" />
          </div>
          <h3 className="text-sm font-bold text-slate-900">No organizations found</h3>
          <p className="text-slate-500 text-xs">Create a business organization independently or run a migration first.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <div
              key={org.id}
              className={`bg-white border hover:border-slate-300 rounded-2xl p-6 transition-all duration-200 shadow-sm flex flex-col justify-between space-y-4 ${
                org.status === 'archived' ? 'opacity-70 bg-slate-50/50' : ''
              }`}
            >
              <div className="space-y-3">
                {/* Badge Row */}
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
                    org.type === 'employer'
                      ? 'bg-blue-50 text-blue-700 border border-blue-100'
                      : org.type === 'sponsor'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                      : 'bg-purple-50 text-purple-700 border border-purple-100'
                  }`}>
                    {org.type}
                  </span>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    org.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-200 text-slate-700'
                  }`}>
                    {org.status}
                  </span>
                </div>

                {/* Name */}
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 tracking-tight leading-tight">
                    {org.name}
                  </h3>
                  {org.legalName && org.legalName !== org.name && (
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5">{org.legalName}</p>
                  )}
                  <p className="text-[11px] text-slate-500 font-mono mt-1 select-all">{org.id}</p>
                </div>

                {/* Meta details */}
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  {org.contactEmail && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{org.contactEmail}</span>
                    </div>
                  )}
                  {org.contactPhone && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span>{org.contactPhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Globe className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="capitalize">{org.entityKind} operator</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-auto">
                <button
                  onClick={() => {
                    setSelectedOrg(org);
                    fetchMembers(org.id);
                    setActiveDetailTab('details');
                  }}
                  className="flex items-center gap-1.5 text-slate-700 hover:text-slate-900 text-xs font-bold"
                >
                  <Eye className="h-4 w-4" />
                  Console Detail
                </button>
                <div className="flex items-center gap-1.5">
                  <button aria-label="Edit organization" title="Edit organization"
                    onClick={() => openEdit(org)}
                    className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  {org.status === 'active' ? (
                    <button aria-label="Archive organization" title="Archive organization"
                      onClick={() => handleArchive(org.id)}
                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  ) : (
                    <button aria-label="Reactivate organization" title="Reactivate organization"
                      onClick={() => handleReactivate(org.id, org.name)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {(showCreateModal || !!editingOrg) && (
        <Modal
          onClose={() => {
            setShowCreateModal(false);
            setEditingOrg(null);
            resetForm();
          }}
          title={editingOrg ? 'Modify Corporate Entity' : 'Establish Corporate Entity'}
        >
          <form onSubmit={editingOrg ? handleUpdate : handleCreate} className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Organization Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. ABC School District"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Official Legal Name</label>
                <input
                  type="text"
                  value={form.legalName}
                  onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                  placeholder="e.g. ABC School District, LLC"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Entity Kind *</label>
                <select
                  value={form.entityKind}
                  disabled={!!editingOrg}
                  onChange={(e) => setForm({ ...form, entityKind: e.target.value as any })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                >
                  <option value="organization">Corporate/Company</option>
                  <option value="individual">Sole Operator/Individual</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Business Role Category *</label>
                <select
                  value={form.type}
                  disabled={!!editingOrg}
                  onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                >
                  <option value="employer">Employer</option>
                  <option value="sponsor">Sponsor</option>
                  <option value="referrer">Referrer</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Primary Contact Email</label>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                  placeholder="e.g. contact@domain.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Primary Contact Phone</label>
                <input
                  type="text"
                  value={form.contactPhone}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  placeholder="e.g. +1 555 019 2831"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Address Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Street Address</label>
                  <input
                    type="text"
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                    placeholder="e.g. 100 Main St"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="e.g. Chicago"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">State/Prov</label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="e.g. IL"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Zip/Postal</label>
                  <input
                    type="text"
                    value={form.zip}
                    onChange={(e) => setForm({ ...form, zip: e.target.value })}
                    placeholder="e.g. 60601"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Country</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="e.g. USA"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingOrg(null);
                  resetForm();
                }}
                className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all shadow-sm"
              >
                {editingOrg ? 'Save Modifications' : 'Create and Sync'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Organization Console Detail Modal */}
      {selectedOrg && (
        <Modal
          onClose={() => setSelectedOrg(null)}
          title={`Business Console: ${selectedOrg.name}`}
        >
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden w-full flex flex-col h-[80vh] max-h-[700px]">
            {/* Tabs */}
            <div className="flex border-b border-slate-200 bg-slate-50 shrink-0">
              {(['details', 'members', 'audit'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveDetailTab(tab)}
                  className={`flex-1 py-3 text-center text-xs font-bold border-b-2 transition-all capitalize ${
                    activeDetailTab === tab
                      ? 'border-slate-900 text-slate-900 bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Scrollable Tab Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {activeDetailTab === 'details' && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Corporate Identity Card</h4>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-slate-400 block font-semibold">Corporate Name</span>
                        <span className="text-slate-900 font-bold">{selectedOrg.name}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block font-semibold">Legal Designation</span>
                        <span className="text-slate-900 font-bold">{selectedOrg.legalName || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block font-semibold">Business Role</span>
                        <span className="text-slate-900 font-bold capitalize">{selectedOrg.type}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block font-semibold">Identity Kind</span>
                        <span className="text-slate-900 font-bold capitalize">{selectedOrg.entityKind}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block font-semibold">Corporate Slug</span>
                        <span className="text-slate-900 font-mono select-all">{selectedOrg.slug || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block font-semibold">Establishment Date</span>
                        <span className="text-slate-900 font-bold">{new Date(selectedOrg.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Primary Location & Address</h4>
                    <div className="text-xs text-slate-800 space-y-1">
                      {selectedOrg.address ? (
                        <>
                          <div className="font-semibold">{selectedOrg.address.street}</div>
                          {selectedOrg.address.apartment && <div>{selectedOrg.address.apartment}</div>}
                          <div>{selectedOrg.address.city}, {selectedOrg.address.state} {selectedOrg.address.zip}</div>
                          <div className="font-bold text-slate-600 uppercase tracking-wider">{selectedOrg.address.country}</div>
                        </>
                      ) : (
                        <div className="text-slate-500 italic">No address provided.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeDetailTab === 'members' && (
                <div className="space-y-4">
                  {/* Actions Bar */}
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Linked Members ({orgMembers.length})</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowAddLinkUserModal(true)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs px-3 py-1.5 rounded-xl transition-all"
                      >
                        Link Existing User
                      </button>
                      <button
                        onClick={() => setShowAddMemberModal(true)}
                        className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-3 py-1.5 rounded-xl transition-all shadow-sm"
                      >
                        Invite New Teammate
                      </button>
                    </div>
                  </div>

                  {membersLoading ? (
                    <div className="space-y-3 py-6">
                      <div className="h-6 w-full bg-slate-100 animate-pulse rounded"></div>
                      <div className="h-6 w-full bg-slate-100 animate-pulse rounded"></div>
                    </div>
                  ) : orgMembers.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 text-xs">
                      No members are linked to this corporate organization. Invite or link a member above.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orgMembers.map((member) => (
                        <div key={member.uid} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 text-xs">
                              {member.firstName ? member.firstName[0] : member.email[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="text-xs font-bold text-slate-950 flex items-center gap-1.5">
                                {member.firstName} {member.lastName}
                                {member.isPrimaryAccount && (
                                  <span className="bg-amber-500/10 text-amber-600 text-[11px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                                    Primary Owner
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 font-medium">{member.email}</div>
                              {member.title && (
                                <div className="text-[11px] text-slate-500 font-semibold">{member.title}</div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {!member.isPrimaryAccount && (
                              <button
                                onClick={() => handleTransferOwnership(member.uid)}
                                className="bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold text-[10px] px-2.5 py-1.5 rounded-lg transition-all"
                              >
                                Make Owner
                              </button>
                            )}
                            <button aria-label="Remove member" title="Remove member"
                              onClick={() => handleRemoveMember(member.uid)}
                              className="bg-rose-50 hover:bg-rose-100 text-rose-700 p-1.5 rounded-lg transition-all"
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeDetailTab === 'audit' && (
                <div className="space-y-3 font-mono text-[10px] text-slate-600 bg-slate-50 p-4 rounded-2xl max-h-[400px] overflow-y-auto">
                  <div className="flex items-center gap-1.5 text-slate-900 font-bold border-b border-slate-200 pb-2 mb-2">
                    <Activity className="h-4 w-4" />
                    Audit Logs
                  </div>
                  <div>&gt; [SYSTEM] Organization loaded in console.</div>
                  <div>&gt; [INFO] Strict workspace isolation enabled.</div>
                </div>
              )}
            </div>

            {/* Bottom Actions */}
            <div className="p-6 border-t border-slate-200 shrink-0 bg-slate-50 flex justify-end">
              <button
                onClick={() => setSelectedOrg(null)}
                className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
              >
                Close Console
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Invite Member Modal */}
      {showAddMemberModal && (
        <Modal
          onClose={() => setShowAddMemberModal(false)}
          title="Invite Teammate / Primary Account"
        >
          <form onSubmit={handleInvitePrimaryAccount} className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">First Name *</label>
                <input
                  type="text"
                  required
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  placeholder="John"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Last Name *</label>
                <input
                  type="text"
                  required
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  placeholder="Doe"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Email Address *</label>
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john.doe@company.com"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Internal Job Title</label>
                <input
                  type="text"
                  value={inviteForm.title}
                  onChange={(e) => setInviteForm({ ...inviteForm, title: e.target.value })}
                  placeholder="e.g. Hiring Manager, Compliance Director"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Workspace Role Permission *</label>
                <select
                  value={inviteForm.workspaceRole}
                  onChange={(e) => setInviteForm({ ...inviteForm, workspaceRole: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                >
                  <option value="member">Workspace Member</option>
                  <option value="viewer">Workspace Viewer</option>
                  <option value="admin">Workspace Admin</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAddMemberModal(false)}
                className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviting}
                className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
              >
                {inviting ? 'Inviting...' : 'Invite Primary Owner'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Link Existing User Modal */}
      {showLinkUserModal && (
        <Modal
          onClose={() => {
            setShowAddLinkUserModal(false);
            setLinkEmail('');
            setLinkedUser(null);
            setLinkTitle('');
          }}
          title="Link Existing Account by Email"
        >
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full p-6 space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">User Email Address</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  placeholder="Search existing email..."
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                />
                <button
                  type="button"
                  onClick={handleSearchUser}
                  disabled={searchingUser}
                  className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 rounded-xl transition-all shrink-0"
                >
                  {searchingUser ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            {linkedUser ? (
              <div className="space-y-4 animate-fade-in">
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-2">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Account Identity Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-slate-400 block font-semibold">First Name</span>
                      <span className="text-slate-900 font-bold">{linkedUser.firstName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-semibold">Last Name</span>
                      <span className="text-slate-900 font-bold">{linkedUser.lastName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-semibold">Current Primary Role</span>
                      <span className="text-slate-900 font-bold capitalize">{linkedUser.primaryRole || 'applicant'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-semibold">Current Workspace</span>
                      <span className="text-slate-900 font-bold select-all truncate">{linkedUser.workspaceId || 'N/A'}</span>
                    </div>
                  </div>

                  {linkedUser.primaryRole && linkedUser.primaryRole !== 'applicant' && (
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-start gap-2 mt-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-[10px] text-amber-800 leading-normal">
                        <strong>Warning:</strong> Linking this account will overwrite their current primary business role (<strong>{linkedUser.primaryRole}</strong>) with <strong>{selectedOrg?.type}</strong> in this Workspace.
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Internal Job Title</label>
                    <input
                      type="text"
                      value={linkTitle}
                      onChange={(e) => setLinkTitle(e.target.value)}
                      placeholder="e.g. Director of Staff"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Workspace Membership Role</label>
                    <select
                      value={linkWorkspaceRole}
                      onChange={(e) => setLinkWorkspaceRole(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold outline-none focus:bg-white"
                    >
                      <option value="member">Workspace Member</option>
                      <option value="viewer">Workspace Viewer</option>
                      <option value="admin">Workspace Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddLinkUserModal(false);
                      setLinkEmail('');
                      setLinkedUser(null);
                    }}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs px-4 py-2.5 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleLinkUser}
                    disabled={linkingUser}
                    className="bg-brand hover:bg-brand-active text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all shadow-sm"
                  >
                    {linkingUser ? 'Linking...' : 'Confirm Linking & Role Conversion'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </Modal>
      )}
    </div>
  );
}
